<script setup lang="ts">
/**
 * What the tutor teaches, one line per level.
 *
 * The profile used to show a row of subject chips and, further down, a separate
 * list of every exam board — three flat lists a parent had to combine
 * themselves. A tutor of Spanish and History with an A Level and a GCSE gave no
 * clue which subject the A Level belonged to.
 *
 * One line per record, with the subject spanning its own lines. Listing the
 * levels inside a single cell packed six or eight of them into one box and made
 * the boards beside them impossible to line up; a row per level puts each level
 * next to the boards actually recorded for it, and the merged subject cell keeps
 * the name from being repeated down the page the way MyTutor's does.
 */
interface TeachingRow {
  level: string
  examboards: string[]
}

interface Teaching {
  subject_id: number
  subject: string
  levels: string[]
  examboards: string[]
  rows?: TeachingRow[]
}

const props = defineProps<{ teaching: Teaching[] }>()

/** A tutor recorded before the per-level breakdown existed still renders. */
const rowsFor = (entry: Teaching): TeachingRow[] =>
  entry.rows?.length
    ? entry.rows
    : entry.levels.map((level) => ({ level, examboards: entry.examboards }))
</script>

<template>
  <div v-if="teaching.length" class="overflow-x-auto rounded-lg border border-line">
    <table class="w-full text-left text-sm">
      <thead>
        <tr class="border-b border-line bg-surface-2">
          <th scope="col" class="px-4 py-3 font-semibold text-ink">Subject</th>
          <th scope="col" class="px-4 py-3 font-semibold text-ink">Level</th>
          <th scope="col" class="px-4 py-3 font-semibold text-ink">Exam board</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="entry in teaching" :key="entry.subject_id">
          <tr
            v-for="(row, index) in rowsFor(entry)"
            :key="`${entry.subject_id}-${row.level}`"
            :class="[
              'align-top',
              index === rowsFor(entry).length - 1 ? 'border-b border-line last:border-0' : '',
            ]"
          >
            <!--
              Written once per subject and spanning its levels, so the eye reads
              "Spanish" once and then runs down what Spanish is taught at.
            -->
            <th
              v-if="index === 0"
              scope="rowgroup"
              :rowspan="rowsFor(entry).length"
              class="border-b border-line px-4 py-3 text-left align-top font-semibold text-ink last:border-0"
            >
              {{ entry.subject }}
            </th>
            <td class="px-4 py-3 text-muted">{{ row.level }}</td>
            <td class="px-4 py-3 text-muted">
              <!--
                A subject can legitimately have no board — primary tutoring, or a
                language taught conversationally — so the cell says so rather
                than leaving a gap that reads as missing data.
              -->
              <span v-if="row.examboards.length">{{ row.examboards.join(', ') }}</span>
              <span v-else class="text-subtle">Any</span>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
